import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  HttpLink,
  split,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { IAuthData } from '@skedulo/horizon-types'
import { logDataDogError } from '../../../../../errors/utils/errors'
import { IApiClientBaseOptions } from '../../types'
import {
  executeGraphQLFetch,
  refetchGraphQLFetch,
  executeGraphQLResourceFetch,
  refetchGraphQLResourceFetch,
} from './service'
import type {
  GraphQLFetchOptions,
  GraphQLFetchServiceResult,
  GraphQLResourceFetchOptions,
  GraphQLResourceFetchServiceResult,
} from './types'
import type { EventBus } from '../../../../events/event-bus'

/**
 * Global type declaration for window.refreshAuth function injected by host app.
 * This function is optional and triggers authentication token refresh.
 */
declare global {
  interface Window {
    refreshAuth?: () => void | Promise<IAuthData>
  }
}

/**
 * Internal hacky type used to access the underlying WebSocket client
 * because Apollo WebSocketLink doesn't expose it publicly.
 */
interface PatchedWebSocketLink {
  subscriptionClient?: {
    client?: WebSocket & {
      addEventListener?: (
        type: string,
        listener: (event: MessageEvent | CloseEvent | Event) => void,
      ) => void
      removeEventListener?: (
        type: string,
        listener: (event: MessageEvent | CloseEvent | Event) => void,
      ) => void
      onmessage?: (event: MessageEvent) => void
    }
    connectionCallback?: ((error?: Record<string, unknown>) => void) | null
    onConnected?: (cb: () => void) => void
    onDisconnected?: (cb: () => void) => void
    onError?: (cb: (error: unknown) => void) => void
    onReconnected?: (cb: () => void) => void
  }
}

export class GraphQLApiClient {
  private auth: IAuthData
  private apolloClient: ApolloClient<NormalizedCacheObject> | null = null
  private readonly eventbus: EventBus<unknown> | null

  // Auth failure handling delay - allows time for refresh to complete
  // Protects against reconnect/refresh storms by preventing immediate retries
  // Assumes auth refresh typically completes within 5 seconds
  private static readonly AUTH_FAILURE_RESET_DELAY_MS = 5000

  private keepAliveIntervalId: ReturnType<typeof setInterval> | null = null
  private restoreSocketOnMessage: (() => void) | null = null
  private socketCleanup: (() => void) | null = null
  private currentSocketId: number | null = null
  private monitoringAttached = false
  private socketSequence = 0
  private lastEmittedWsEvent: string | null = null
  private handlingAuthFailure = false
  private authFailureTimeoutId: ReturnType<typeof setTimeout> | null = null
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null

  constructor(options: IApiClientBaseOptions, auth: IAuthData) {
    this.auth = auth
    this.eventbus = options.eventBus ?? null
  }

  /**
   * Cleanup method to remove event listeners and clear socket-related state.
   * Prevents memory leaks when the client is destroyed.
   */
  destroy(): void {
    if (this.authFailureTimeoutId) {
      clearTimeout(this.authFailureTimeoutId)
      this.authFailureTimeoutId = null
    }
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }
    this.handlingAuthFailure = false

    // Clear keep-alive interval
    if (this.keepAliveIntervalId) {
      clearInterval(this.keepAliveIntervalId)
      this.keepAliveIntervalId = null
    }

    // Restore original socket message handler
    if (this.restoreSocketOnMessage) {
      try {
        this.restoreSocketOnMessage()
      } catch (err) {
        logDataDogError(err as Error, {
          component: 'GraphQL Client',
          description: 'Failed to restore socket onmessage in destroy',
        })
      }
      this.restoreSocketOnMessage = null
    }

    // Clean up socket event listeners
    if (this.socketCleanup) {
      try {
        this.socketCleanup()
      } catch (err) {
        logDataDogError(err as Error, {
          component: 'GraphQL Client',
          description: 'Failed to cleanup socket listeners in destroy',
        })
      }
      this.socketCleanup = null
    }

    this.monitoringAttached = false
  }

  /**
   * Emits WebSocket-related events with deduplication.
   * Deduplication prevents event spam from rapid state changes (e.g., multiple
   * keep-alive messages or repeated connection errors).
   *
   * Tradeoff: This makes events edge-triggered rather than level-triggered.
   * If the same event type occurs multiple times, only the first is emitted.
   * This can suppress recovery signals or repeated stale warnings if the same
   * topic is emitted again before state changes.
   *
   * Known limitation: If a stale connection recovers and becomes stale again
   * without an intervening event, the second stale event may be suppressed.
   */
  private emitWsEvent(topic: string, data?: unknown): void {
    if (this.lastEmittedWsEvent === topic) return
    this.lastEmittedWsEvent = topic
    if (data !== undefined) {
      this.eventbus?.emit({ topic, data })
    } else {
      this.eventbus?.emit({ topic })
    }
  }

  /**
   * Gets the API token from localStorage if available, otherwise falls back to the auth object.
   * This ensures we always use the latest token, even if it was updated in localStorage.
   */
  private get apiToken() {
    if (typeof window !== 'undefined') {
      try {
        const authData = JSON.parse(localStorage.getItem('auth') || 'null')
        const newToken = authData?.skedApiAccessToken
        if (newToken) {
          return newToken
        }
      } catch (err) {
        // If localStorage read fails, fall back to auth object
        logDataDogError(err as Error, {
          component: 'GraphQL Client',
          description: 'Failed to read token from localStorage',
        })
      }
    }
    return this.auth.skedApiAccessToken
  }

  /**
   * Handles auth failure by triggering auth refresh and setting a guard flag
   * to prevent reconnect storms. Calls window.refreshAuth() if available.
   */
  private handleAuthFailure(): void {
    // Guard against concurrent auth failure handling
    if (this.handlingAuthFailure) {
      return
    }

    this.handlingAuthFailure = true

    // Trigger auth refresh if available
    if (
      typeof window !== 'undefined' &&
      typeof (window as any).refreshAuth === 'function'
    ) {
      try {
        ;(window as any).refreshAuth?.()
      } catch (err) {
        logDataDogError(err as Error, {
          component: 'GraphQL Client',
          description: 'Failed to call window.refreshAuth()',
        })
      }
    }

    // Track timeout so it can be cleared in destroy()
    this.authFailureTimeoutId = setTimeout(() => {
      this.handlingAuthFailure = false
      this.authFailureTimeoutId = null
    }, GraphQLApiClient.AUTH_FAILURE_RESET_DELAY_MS)
  }

  /**
   * Checks if a connection error is auth-related based on the error payload.
   * Checks for errorType === 'authentication_error' which is what the server sends.
   */
  private isAuthError(errorPayload: unknown): boolean {
    if (
      !errorPayload ||
      typeof errorPayload !== 'object' ||
      !('errorType' in (errorPayload as Record<string, unknown>))
    ) {
      return false
    }

    const payload = errorPayload as {
      errorType?: string
      message?: string
    }
    return payload.errorType === 'authentication_error'
  }

  private get graphqlHttpEndpoint() {
    const baseUrl = this.auth.servers.apiServer
    const endpoint = 'graphql/graphql'
    return baseUrl
      ? `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}${endpoint}`
      : endpoint
  }

  private get graphqlWsEndpoint() {
    const baseUrl = this.auth.servers.apiServer
    if (!baseUrl) return ''
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws'
    const baseWithoutProtocol = baseUrl.replace(/^https?:\/\//, '')
    const endpoint = 'graphql/subscriptions'
    return `${wsProtocol}://${baseWithoutProtocol}${
      baseUrl.endsWith('/') ? '' : '/'
    }${endpoint}`
  }

  getClient(): ApolloClient<NormalizedCacheObject> {
    if (!this.apolloClient) {
      this.apolloClient = this.createClient()
    }
    return this.apolloClient
  }

  async fetch<T = unknown>(
    options: GraphQLFetchOptions,
  ): Promise<GraphQLFetchServiceResult<T>> {
    const client = this.getClient()
    return executeGraphQLFetch<T>(client, options)
  }

  async refetch<T = unknown>(
    options: GraphQLFetchOptions,
    overrideVariables?: OperationVariables,
  ): Promise<ApolloQueryResult<T>> {
    const client = this.getClient()
    return refetchGraphQLFetch<T>(client, options, overrideVariables)
  }

  async fetchResource<T = unknown>(
    options: GraphQLResourceFetchOptions,
  ): Promise<GraphQLResourceFetchServiceResult<T>> {
    const client = this.getClient()
    return executeGraphQLResourceFetch<T>(client, options)
  }

  async refetchResource<T = unknown>(
    options: GraphQLResourceFetchOptions,
    overrideVariables?: OperationVariables,
  ): Promise<ApolloQueryResult<T>> {
    const client = this.getClient()
    return refetchGraphQLResourceFetch<T>(client, options, overrideVariables)
  }

  private createClient(): ApolloClient<NormalizedCacheObject> {
    const httpLink = new HttpLink({
      uri: this.graphqlHttpEndpoint,
    })

    // WebSocketLink with reconnect enabled
    //
    // IMPORTANT: Connection accumulation behavior
    // subscriptions-transport-ws@0.11.0 can create multiple WebSocket connections
    // over time when reconnect: true. This is expected behavior:
    // - Old sockets may remain open and visible in DevTools
    // - Each reconnect replays active subscriptions (start frames)
    // - The library manages connection lifecycle internally
    //
    // TODO: This cannot be fully fixed without migrating to a newer library
    // (e.g., graphql-ws) that has better connection lifecycle management.
    const wsLink = new WebSocketLink({
      uri: this.graphqlWsEndpoint,
      options: {
        reconnect: true,
        // connectionParams is called on each connection/reconnection
        // Reads fresh token from localStorage to ensure we always use the latest token
        connectionParams: () => ({
          Authorization: `Bearer ${this.apiToken}`,
        }),
        lazy: true,
        timeout: 30000,
      },
    })

    // --- Keep-Alive Monitoring ---
    // NOTE: Keep-alive health tracking is heuristic and best-effort.
    // Missing keep-alive messages do not guarantee WebSocket failure.
    // Stale detection is based on time since last keep-alive, but:
    // - lastKeepAlive is shared across socket lifetimes
    // - Orphaned sockets can stop emitting keep-alive messages
    // - Time-based stale detection can fire even when a newer socket is healthy
    const KEEP_ALIVE_INTERVAL_MS = 30000
    const RECONNECT_MONITORING_DELAY_MS = 2000
    let lastKeepAlive = Date.now()

    const onConnectionStale = () => {
      this.emitWsEvent('graphql:ws:stale')
    }

    const clearKeepAlive = () => {
      if (this.keepAliveIntervalId) {
        clearInterval(this.keepAliveIntervalId)
        this.keepAliveIntervalId = null
      }
      if (this.restoreSocketOnMessage) {
        try {
          this.restoreSocketOnMessage()
        } catch (err) {
          logDataDogError(err as Error, {
            component: 'GraphQL Client',
            description: 'restoreSocketOnMessage failed',
          })
        } finally {
          this.restoreSocketOnMessage = null
        }
      }
      if (this.socketCleanup) {
        try {
          this.socketCleanup()
        } catch (err) {
          logDataDogError(err as Error, {
            component: 'GraphQL Client',
            description: 'socketCleanup failed',
          })
        } finally {
          this.socketCleanup = null
        }
      }
      this.monitoringAttached = false
    }

    const checkConnectionStale = () => {
      if (Date.now() - lastKeepAlive > KEEP_ALIVE_INTERVAL_MS) {
        onConnectionStale()
      }
    }

    const attachSocketMonitoring = () => {
      // prevent duplicate monitoring attachments
      if (this.monitoringAttached) return
      this.monitoringAttached = true

      try {
        const ws = wsLink as unknown as PatchedWebSocketLink
        const client = ws.subscriptionClient?.client

        if (!client) {
          logDataDogError(
            new Error(
              'GraphQL Layer - No WebSocket client found for monitoring',
            ),
            { component: 'GraphQL Client' },
          )
          this.monitoringAttached = false
          return
        }

        const socketId = ++this.socketSequence
        this.currentSocketId = socketId

        const originalOnMessage = client.onmessage
        client.onmessage = (event: MessageEvent) => {
          try {
            const parsed = JSON.parse(event.data as string)
            if (parsed?.type === 'ka') {
              // Update keep-alive timestamp for current socket
              lastKeepAlive = Date.now()
              this.emitWsEvent('graphql:ws:keepAlive')
            } else if (parsed?.type === 'connection_error') {
              // Check if this is an auth-related error
              if (this.isAuthError(parsed.payload)) {
                // Guard: Don't handle auth failure if already handling one
                if (!this.handlingAuthFailure) {
                  this.handleAuthFailure()
                }
              }
            }
          } catch (err) {
            logDataDogError(err as Error, {
              component: 'GraphQL Client',
              description: 'Error parsing WebSocket message',
            })
          }
          originalOnMessage?.call(client, event)
        }

        this.restoreSocketOnMessage = () => {
          ;(client as any).onmessage = originalOnMessage as any
        }

        // Start stale checker
        this.keepAliveIntervalId = setInterval(
          checkConnectionStale,
          KEEP_ALIVE_INTERVAL_MS,
        )

        // Clean up when closed or errored
        const handleClose = (closeEvent: CloseEvent) => {
          if (this.currentSocketId === socketId) {
            clearKeepAlive()
            // Check if close was due to auth failure
            // WebSocket close codes used:
            // - 1008: Policy violation (often used by servers for auth failures)
            // - 1002: Protocol error (sometimes used for auth-related issues)
            //
            // NOTE: These codes are not guaranteed to be auth-related.
            // We check the close reason to reduce false positives.
            // Server-specific behavior is assumed - some servers may use different codes.
            //
            // Risk: False positives could trigger unnecessary auth refresh attempts.
            // This is mitigated by checking the close reason for auth-related keywords.
            if (
              !this.handlingAuthFailure &&
              (closeEvent.code === 1008 || closeEvent.code === 1002)
            ) {
              // Check if there's a reason that indicates auth failure
              // This reduces false positives from non-auth related closes
              const reason = closeEvent.reason?.toLowerCase() || ''
              if (
                reason.includes('unauthorized') ||
                reason.includes('authentication') ||
                reason.includes('token') ||
                reason.includes('expired')
              ) {
                this.handleAuthFailure()
              }
            }
          }
        }

        const handleError = () => {
          if (this.currentSocketId === socketId) {
            clearKeepAlive()
          }
        }

        client.addEventListener?.('close', handleClose)
        client.addEventListener?.('error', handleError)
        this.socketCleanup = () => {
          client.removeEventListener?.('close', handleClose)
          client.removeEventListener?.('error', handleError)
        }

        // Safe reconnect handler
        let reconnecting = false
        ws.subscriptionClient?.onReconnected?.(() => {
          if (reconnecting) return
          reconnecting = true
          clearKeepAlive()
          // Reset keep-alive timestamp on successful reconnect to avoid perma-stale state
          // This ensures the new socket doesn't inherit stale timing from the previous socket
          lastKeepAlive = Date.now()
          // Emit synthetic keep-alive to signal healthy reconnection
          this.emitWsEvent('graphql:ws:keepAlive')
          this.reconnectTimeoutId = setTimeout(() => {
            this.reconnectTimeoutId = null
            reconnecting = false
            attachSocketMonitoring()
          }, RECONNECT_MONITORING_DELAY_MS)
        })
      } catch (err) {
        logDataDogError(err as Error, {
          component: 'GraphQL Client',
          description: 'Failed to attach socket monitoring',
        })
        this.emitWsEvent('graphql:ws:monitoringError', err)
        this.monitoringAttached = false
      }
    }

    // With lazy: true, the WebSocket doesn't exist yet at client creation time.
    // We need to handle two lifecycle events:
    // 1. onConnected: attach keep-alive monitoring once the socket is available
    // 2. connectionCallback: catch auth errors on first connection attempt, before
    //    monitoring is attached (onConnected only fires on connection_ack, not connection_error)
    const ws = wsLink as unknown as PatchedWebSocketLink
    ws.subscriptionClient?.onConnected?.(() => {
      if (!this.monitoringAttached) {
        lastKeepAlive = Date.now()
        attachSocketMonitoring()
      }
    })

    // subscriptions-transport-ws routes connection_error payloads to connectionCallback,
    // NOT to onError or onmessage. Without this, a failed first connection (e.g. expired token)
    // would go undetected since attachSocketMonitoring hasn't patched onmessage yet.
    if (ws.subscriptionClient) {
      ws.subscriptionClient.connectionCallback = (
        error?: Record<string, unknown>,
      ) => {
        if (error && this.isAuthError(error) && !this.handlingAuthFailure) {
          this.handleAuthFailure()
        }
      }
    }

    // --- Auth link ---
    const authLink = setContext((_, { headers }) => ({
      headers: {
        ...headers,
        authorization: `Bearer ${this.apiToken}`,
      },
    }))

    // --- Split link for subscriptions vs HTTP ---
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      },
      wsLink,
      httpLink,
    )

    // --- Apollo Client instance ---
    return new ApolloClient({
      link: authLink.concat(splitLink),
      cache: new InMemoryCache({
        dataIdFromObject(responseObject) {
          const typename = responseObject.__typename

          // Treat all subscription payload types as event-only to be handled by the subscription hook (onData callback)
          // Otherwise, the data will automatically update in the cache.
          // This can cause issues with the subscription hook if the data is older than whats in the cache.
          if (
            typeof typename === 'string' &&
            typename.startsWith('SchemaSubscription')
          ) {
            return undefined
          }

          if (responseObject.UID) return String(responseObject.UID)
          if (responseObject.id) return String(responseObject.id)
          return undefined
        },
      }),
      defaultOptions: {
        watchQuery: { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
        query: { fetchPolicy: 'cache-first', errorPolicy: 'all' },
      },
    })
  }
}
